import { Component, Element, Host, h, Prop, State } from "@stencil/core";

import api from "../../utils/api";
import { debounce } from "../../utils/debounce";
@Component({
    tag: "ux-iws-load-more",
    styleUrl: "load-more.scss",
    shadow: false,
})
export class LoadMore {
    /**if set, the displayed element will have the css styles fo a link */
    @Prop() link: boolean;
    /** the css selector of the dom element where the response data should be appended */
    @Prop() targetElementSelector: string = "";
    /** aligns the content of the slot either to left or right (default is center) */
    @Prop() alignContent: "left" | "right";

    @Element() loadMore: HTMLUxIwsLoadMoreElement;

    @State() isLoading: boolean = false;
    @State() error: boolean = false;

    @State() url: string = null;

    private targetElement: HTMLElement;
    private contentObserver: MutationObserver = new MutationObserver(
        debounce(() => this.updateUrl(), 200)
    );

    private updateUrl = () => {
        const linkNextElement =
            this.targetElement.querySelector("link[rel=next]");
        if (!linkNextElement) {
            this.url = null;
            return;
        }
        this.url = linkNextElement.getAttribute("href") || null;
    };

    componentWillLoad() {
        this.targetElement = document.querySelector(this.targetElementSelector);
        this.updateUrl();
        this.contentObserver.observe(this.targetElement, {
            childList: true,
            subtree: true,
            attributes: true,
        });
    }

    disconnectedCallback() {
        this.contentObserver.disconnect();
    }

    private requestContent = async (e: Event) => {
        e.preventDefault();
        if (this.url === "" || this.url === null) return;
        this.error = false;
        this.isLoading = true;

        try {
            const response: string = await api<string>(this.url);
            this.targetElement.querySelector("link[rel=next]")?.remove();
            this.updateContent(response);
            this.isLoading = false;
        } catch {
            this.isLoading = false;
            this.error = true;
        }
    };

    private updateContent = (htmlResponse: string) => {
        const targetElement = document.querySelector(
            this.targetElementSelector
        );
        targetElement.insertAdjacentHTML("beforeend", htmlResponse);
    };

    render() {
        const { alignContent, isLoading, link, url } = this;
        const baseClass = "ux-iws-load-more";
        const Element = link ? "a" : "ux-button";

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--left`]: alignContent === "left",
                    [`${baseClass}--right`]: alignContent === "right",
                    [`${baseClass}--hidden`]: url === null,
                }}
            >
                <div
                    class={{
                        "ux-text-status--red": true,
                        [`${baseClass}__error`]: true,
                        [`${baseClass}__error--active`]: this.error,
                    }}
                >
                    <slot name="error"></slot>
                </div>
                {isLoading && (
                    <div class={`${baseClass}__loading`}>
                        <ux-loader variant="spinner"></ux-loader>
                    </div>
                )}
                <Element
                    href={link ? "#" : null}
                    class={!link ? "" : "ux-link ux-text-link--green"}
                    onClick={(e) => this.requestContent(e)}
                    data-testid="load-more"
                >
                    <slot />
                </Element>
            </Host>
        );
    }
}
